import { usePageTitle } from "@/hooks/use-page-title";
import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useMedicalConditionsActive, QUERY_KEYS } from "@/hooks/use-queries";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Wallet, Users, Building2, Home, HeartPulse, Loader2, Search, Gift } from "lucide-react";
import type { MedicalCondition } from "@shared/schema";

const createWalletSchema = z.object({
  walletType: z.enum(["individual", "family", "community", "organization", "condition"], {
    errorMap: () => ({ message: "Please select a wallet type" })
  }),
  patientName: z.string().min(2, "Name must be at least 2 characters"),
  organizationName: z.string().optional(),
  conditionId: z.string().optional(),
  category: z.string().optional(),
  description: z.string().optional(),
  goalAmount: z.coerce.number().min(0, "Goal must be at least $0").optional(),
}).refine((data) => {
  if (data.walletType === "organization" && !data.organizationName) {
    return false;
  }
  return true;
}, {
  message: "Organization name is required for organization wallets",
  path: ["organizationName"],
}).refine((data) => {
  if (data.walletType === "condition" && !data.conditionId) {
    return false;
  }
  return true;
}, {
  message: "Please select a medical condition",
  path: ["conditionId"],
}).refine((data) => {
  if (data.walletType !== "condition" && !data.category) {
    return false;
  }
  return true;
}, {
  message: "Please select a health category",
  path: ["category"],
});

type CreateWalletFormValues = z.infer<typeof createWalletSchema>;

const categories = [
  { value: "diabetes", label: "Diabetes Management" },
  { value: "cancer", label: "Cancer Treatment" },
  { value: "pediatric", label: "Pediatric Care" },
  { value: "cardiac", label: "Cardiac Care" },
  { value: "respiratory", label: "Respiratory Conditions" },
  { value: "rheumatologic", label: "Rheumatologic Conditions" },
  { value: "infectious", label: "Infectious Diseases" },
  { value: "general", label: "General Healthcare" },
];

function ConditionSelector({
  conditions,
  isLoading,
  selectedId,
  onSelect,
}: {
  conditions: MedicalCondition[];
  isLoading: boolean;
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredConditions = conditions.filter((c) =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.description && c.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const groupedConditions = filteredConditions.reduce<Record<string, MedicalCondition[]>>((groups, condition) => {
    const cat = condition.category;
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(condition);
    return groups;
  }, {});

  const categoryLabels: Record<string, string> = {
    chronic: "Chronic Conditions",
    cancer: "Cancer",
    pediatric: "Pediatric",
    mental_health: "Mental Health",
    rare_disease: "Rare Diseases",
    cardiac: "Cardiac",
    respiratory: "Respiratory",
    infectious: "Infectious Diseases",
    neurological: "Neurological",
    autoimmune: "Autoimmune",
    endocrine: "Endocrine",
    general: "General",
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span>Loading conditions...</span>
      </div>
    );
  }

  if (conditions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No predefined conditions available. Please contact support.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search conditions..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
          data-testid="input-condition-search"
        />
      </div>
      <div className="max-h-64 overflow-y-auto border border-border rounded-md">
        {Object.keys(groupedConditions).length === 0 ? (
          <p className="text-sm text-muted-foreground p-4 text-center">No conditions match your search</p>
        ) : (
          Object.entries(groupedConditions)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([category, items]) => (
              <div key={category}>
                <div className="sticky top-0 bg-muted/80 backdrop-blur-sm px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider z-10">
                  {categoryLabels[category] || category}
                </div>
                {items
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((condition) => {
                    const isSelected = selectedId === condition.id;
                    return (
                      <button
                        key={condition.id}
                        type="button"
                        onClick={() => onSelect(condition.id)}
                        className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors cursor-pointer ${
                          isSelected
                            ? "bg-primary/10"
                            : "hover-elevate"
                        }`}
                        data-testid={`condition-item-${condition.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm font-medium ${isSelected ? "text-primary" : ""}`}>
                              {condition.name}
                            </span>
                            {condition.isFeatured && (
                              <Badge variant="secondary" className="text-xs">Featured</Badge>
                            )}
                          </div>
                          {condition.description && (
                            <p className="text-xs text-muted-foreground mt-0.5 truncate">
                              {condition.description}
                            </p>
                          )}
                        </div>
                        {isSelected && (
                          <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
              </div>
            ))
        )}
      </div>
    </div>
  );
}

export default function CreateWalletPage() {
  usePageTitle("Create Health Wallet", "Set up a new individual, family, or community health wallet to start receiving healthcare contributions.");
  const [, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { toast } = useToast();

  const queryParams = new URLSearchParams(window.location.search);
  const prefilledType = queryParams.get("type") || "individual";
  const prefilledConditionId = queryParams.get("conditionId") || "";

  const [selectedType, setSelectedType] = useState<string>(prefilledType);

  const form = useForm<CreateWalletFormValues>({
    mode: 'onBlur',
    resolver: zodResolver(createWalletSchema),
    defaultValues: {
      walletType: prefilledType as CreateWalletFormValues["walletType"],
      patientName: "",
      conditionId: prefilledConditionId,
      category: "",
      description: "",
      goalAmount: 0,
    },
  });

  const { data: medicalConditions = [], isLoading: conditionsLoading } = useMedicalConditionsActive({
    enabled: selectedType === "condition",
  });

  useEffect(() => {
    if (user?.firstName && user?.lastName) {
      const currentName = form.getValues("patientName");
      if (!currentName) {
        form.setValue("patientName", `${user.firstName} ${user.lastName}`);
      }
    }
  }, [user, form]);

  useEffect(() => {
    if (prefilledConditionId && medicalConditions.length > 0) {
      const condition = medicalConditions.find((c) => c.id === prefilledConditionId);
      if (condition) {
        form.setValue("conditionId", condition.id, { shouldValidate: true });
        form.setValue("category", condition.category, { shouldValidate: true });
      }
    }
  }, [prefilledConditionId, medicalConditions, form]);

  const handleConditionSelect = (conditionId: string) => {
    form.setValue("conditionId", conditionId, { shouldValidate: true });
    const condition = medicalConditions.find((c) => c.id === conditionId);
    if (condition) {
      form.setValue("category", condition.category, { shouldValidate: true });
    }
  };

  const createMutation = useMutation({
    mutationFn: async (values: CreateWalletFormValues) => {
      const res = await apiRequest("POST", "/api/wallet", {
        ...values,
        goalAmount: values.goalAmount ? Math.round(values.goalAmount * 100) : 0,
      });
      const wallet = await res.json();
      // 201 = newly created, 200 = existing wallet returned by server
      return { wallet, isNew: res.status === 201 };
    },
    onSuccess: ({ isNew }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.wallet });
      if (isNew) {
        toast({ title: "Wallet created!" });
      } else {
        toast({
          title: "You already have a wallet. Redirecting to your existing wallet.",
        });
      }
      setLocation("/wallet");
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create wallet",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: CreateWalletFormValues) => {
    createMutation.mutate(values);
  };

  const { data: employerEnrollmentData } = useQuery<{ enrollment: any; employer: any } | null>({
    queryKey: ["/api/employer/my-enrollment"],
    retry: false,
  });
  const employerPepm = employerEnrollmentData?.employer?.monthlyPerEmployeeCents ?? 0;
  const employerName = employerEnrollmentData?.employer?.companyName ?? "";

  useEffect(() => {
    if (!authLoading && !user) {
      setLocation("/auth");
    }
  }, [user, authLoading, setLocation]);

  if (authLoading || !user) {
    return null;
  }

  const selectedCondition = medicalConditions.find((c) => c.id === form.watch("conditionId"));

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Create Your Health Wallet</h1>
        <p className="text-muted-foreground">
          Set up your wallet to start receiving contributions and managing healthcare payments
        </p>
      </div>

      {employerPepm > 0 && (
        <div
          data-testid="employer-pepm-banner"
          className="mb-6 flex items-start gap-3 rounded-md border border-primary/30 bg-primary/5 p-4"
        >
          <Gift className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div>
            <p className="font-semibold text-sm">
              Your employer contributes ${(employerPepm / 100).toFixed(0)}/month toward your healthcare
            </p>
            <p className="text-sm text-muted-foreground mt-0.5">
              {employerName ? `${employerName} is` : "Your employer is"} paying ${(employerPepm / 100).toFixed(2)} per month on your behalf.
              Create your wallet now to start receiving these contributions.
            </p>
          </div>
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Wallet Type Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl sm:text-2xl">Wallet Type</CardTitle>
              <CardDescription>Choose the type of wallet that best fits your needs</CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="walletType"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={(value) => {
                          field.onChange(value);
                          setSelectedType(value);
                          if (value !== "condition") {
                            form.setValue("conditionId", "");
                            form.setValue("category", "");
                          }
                        }}
                        className="space-y-4"
                      >
                        <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover-elevate cursor-pointer">
                          <RadioGroupItem value="individual" id="individual" className="mt-1" />
                          <Label htmlFor="individual" className="cursor-pointer flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Wallet className="w-5 h-5 text-primary" />
                              <span className="font-semibold">Individual Wallet</span>
                            </div>
                            <p className="text-sm text-muted-foreground">Personal health savings for yourself</p>
                          </Label>
                        </div>

                        <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover-elevate cursor-pointer">
                          <RadioGroupItem value="family" id="family" className="mt-1" />
                          <Label htmlFor="family" className="cursor-pointer flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Users className="w-5 h-5 text-primary" />
                              <span className="font-semibold">Family Wallet</span>
                            </div>
                            <p className="text-sm text-muted-foreground">Shared wallet for family members</p>
                          </Label>
                        </div>

                        <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover-elevate cursor-pointer">
                          <RadioGroupItem value="community" id="community" className="mt-1" />
                          <Label htmlFor="community" className="cursor-pointer flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Home className="w-5 h-5 text-primary" />
                              <span className="font-semibold">Community Wallet</span>
                            </div>
                            <p className="text-sm text-muted-foreground">Pool resources for neighborhood or association</p>
                          </Label>
                        </div>

                        <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover-elevate cursor-pointer">
                          <RadioGroupItem value="organization" id="organization" className="mt-1" />
                          <Label htmlFor="organization" className="cursor-pointer flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Building2 className="w-5 h-5 text-primary" />
                              <span className="font-semibold">Organization Wallet</span>
                            </div>
                            <p className="text-sm text-muted-foreground">For churches, nonprofits, or cooperatives</p>
                          </Label>
                        </div>

                        <div className="flex items-start space-x-3 p-4 rounded-lg border border-border hover-elevate cursor-pointer">
                          <RadioGroupItem value="condition" id="condition" className="mt-1" />
                          <Label htmlFor="condition" className="cursor-pointer flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <HeartPulse className="w-5 h-5 text-primary" />
                              <span className="font-semibold">Condition Wallet</span>
                            </div>
                            <p className="text-sm text-muted-foreground">Fund care for a specific medical condition</p>
                          </Label>
                        </div>
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Wallet Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl sm:text-2xl">Wallet Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {selectedType === "organization" && (
                <FormField
                  control={form.control}
                  name="organizationName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organization Name</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., Community Health Church" data-testid="input-organization-name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {selectedType === "condition" && (
                <FormField
                  control={form.control}
                  name="conditionId"
                  render={() => (
                    <FormItem>
                      <FormLabel>Medical Condition</FormLabel>
                      <FormDescription>
                        Select the condition this wallet will fund care for
                      </FormDescription>
                      <FormControl>
                        <ConditionSelector
                          conditions={medicalConditions}
                          isLoading={conditionsLoading}
                          selectedId={form.watch("conditionId")}
                          onSelect={handleConditionSelect}
                        />
                      </FormControl>
                      {selectedCondition && (
                        <div className="mt-2 p-3 rounded-md bg-muted/50 border border-border">
                          <p className="text-sm font-medium">{selectedCondition.name}</p>
                          {selectedCondition.description && (
                            <p className="text-xs text-muted-foreground mt-1">{selectedCondition.description}</p>
                          )}
                        </div>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="patientName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {selectedType === "organization" ? "Primary Contact Name" : "Patient/Beneficiary Name"}
                    </FormLabel>
                    <FormControl>
                      <Input placeholder="Full name" data-testid="input-patient-name" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {selectedType !== "condition" && (
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Health Category</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger data-testid="select-category">
                            <SelectValue placeholder="Select a health category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.value} value={cat.value}>
                              {cat.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description (Optional)</FormLabel>
                    <FormDescription>Tell your story or describe your healthcare situation</FormDescription>
                    <FormControl>
                      <Textarea
                        placeholder="Share your health journey or why you're setting up this wallet..."
                        className="resize-none"
                        data-testid="input-description"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="goalAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Funding Goal (Optional)</FormLabel>
                    <FormDescription>Set a target amount you'd like to raise</FormDescription>
                    <FormControl>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">$</span>
                        <Input
                          type="number"
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                          data-testid="input-goal-amount"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              type="submit"
              size="lg"
              disabled={createMutation.isPending}
              data-testid="button-create-wallet"
            >
              {createMutation.isPending ? "Creating..." : "Create Wallet"}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => setLocation("/wallet")}
              disabled={createMutation.isPending}
              data-testid="button-cancel-wallet"
            >
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
